/**
 * Màn khách chọn ảnh cần sửa.
 *
 * Khách mở trên điện thoại, không có tài khoản, và thường vừa xem vừa làm việc
 * khác. Nên: ô tick to, số đếm luôn nhìn thấy, và nút chốt dính đáy màn hình
 * để không phải cuộn hết vài trăm tấm mới bấm được.
 *
 * Chốt xong là KHOÁ: thợ đang ngồi sửa không được đổi việc dưới tay. Muốn đổi
 * thì nhắn studio — câu đó phải nói rõ trên màn, kẻo khách bấm mãi không được
 * mà không hiểu vì sao.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PhotoMasonry } from "@/components/photo-grid/PhotoMasonry";
import { Check, ImageOff, Lock } from "lucide-react";

import { Button } from "@/ui/button";
import { Skeleton } from "@/ui/skeleton";
import {
  albumShareKeys,
  commitPhotoSelection,
  openPhotoSelection,
  selectPhoto,
  type PhotoSelectionAlbum,
} from "./api";

export function PhotoSelectionPage({ token }: { token: string }) {
  const qc = useQueryClient();
  const key = albumShareKeys.selection(token);

  const albumQ = useQuery({
    queryKey: key,
    queryFn: () => openPhotoSelection(token),
    enabled: Boolean(token),
    retry: false,
  });

  const pick = useMutation({
    mutationFn: (v: { photoId: string; selected: boolean }) =>
      selectPhoto(token, v.photoId, v.selected),
    // Ghi thẳng kết quả máy chủ vào cache thay vì gọi lại: mỗi lượt gọi sinh
    // URL ký mới cho cả album, tick mười tấm là mười lượt ký lại toàn bộ.
    onSuccess: (data) => qc.setQueryData(key, data),
  });

  const commit = useMutation({
    mutationFn: () => commitPhotoSelection(token),
    onSuccess: (data) => qc.setQueryData(key, data),
  });

  if (!token) {
    return <Message title="Thiếu link" detail="Chị mở lại đường link studio đã gửi giúp em ạ." />;
  }
  if (albumQ.isLoading) return <SelectionSkeleton />;
  if (albumQ.isError) {
    return <Message title="Không mở được" detail={(albumQ.error as Error).message} />;
  }

  const album = albumQ.data as PhotoSelectionAlbum;
  if (album.photos.length === 0) {
    return (
      <Message
        title="Ảnh đang được chuẩn bị"
        detail="Studio sẽ báo chị ngay khi ảnh sẵn sàng để chọn ạ."
      />
    );
  }

  const busy = pick.isPending || commit.isPending;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 pb-28 pt-6 md:px-6">
      <header className="mb-4 space-y-1">
        <h1 className="text-2xl font-semibold md:text-3xl">Chọn ảnh cần chỉnh sửa</h1>
        <p className="text-sm text-muted-foreground">
          Đơn {album.orderCode} · {album.customerName} · {album.photos.length} ảnh
        </p>
      </header>

      {album.locked ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border bg-muted/50 p-3">
          <Lock className="mt-0.5 h-5 w-5 flex-none text-muted-foreground" aria-hidden />
          <div className="text-sm">
            <p>
              Chị đã chốt <strong>{album.selectedQty} ảnh</strong>. Studio đang chỉnh sửa ạ. Nếu chị
              muốn đổi, nhắn studio để mở lại giúp em nhé.
            </p>
            {/* Màn này KHÔNG biết đơn còn nợ hay không (album chia sẻ không mang
                ô giá nào), nên câu chữ chỉ mời sang xem — cổng đơn hàng mới là
                nơi hiện đúng số phải trả, và chỉ hiện khi thật sự còn nợ. */}
            <a
              href="/booking/me"
              className="mt-2 inline-flex min-h-11 items-center font-semibold text-[var(--color-brand)] underline"
            >
              Về trang của bạn để xem đơn và thanh toán
            </a>
          </div>
        </div>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">
          Chị bấm vào ảnh muốn studio chỉnh sửa. Chọn xong bấm nút chốt ở dưới ạ.
        </p>
      )}

      <SelectionGrid
        album={album}
        busy={busy}
        onToggle={(photoId, selected) => pick.mutate({ photoId, selected })}
      />

      {!album.locked && (
        <CommitBar
          selectedQty={album.selectedQty}
          busy={busy}
          error={((commit.error ?? pick.error) as Error | null)?.message ?? ""}
          onCommit={() => commit.mutate()}
        />
      )}
    </div>
  );
}

function SelectionGrid({
  album,
  busy,
  onToggle,
}: {
  album: PhotoSelectionAlbum;
  busy: boolean;
  onToggle: (photoId: string, selected: boolean) => void;
}) {
  return (
    // Lưới dùng chung với mười màn kia: ô ôm ĐÚNG KHỔ từng tấm, không cắt tấm
    // nào. Trước 26/09/2026 màn này ép mọi tấm về khổ 3:4 — khách chọn ảnh dọc
    // thì thấy một tấm đã bị xén hai bên.
    //
    // Cả ô LÀ công tắc tích chọn (aria-pressed), không phải nút mở ảnh. Dấu
    // tích nằm ở lớp phủ; màu KHÔNG chạm mép ảnh nữa.
    <PhotoMasonry
      label="Lưới ảnh khách chọn"
      tiles={album.photos.map((p) => ({
        id: p.photoId,
        url: p.url,
        width: p.width ?? null,
        height: p.height ?? null,
        flags: [],
        caption: String(p.seq).padStart(2, "0"),
        label: `Ảnh ${p.seq}${p.selected ? " — đang chọn" : ""}`,
        pressed: p.selected,
        disabled: album.locked || busy,
        ...(p.selected
          ? {
              overlay: (
                <span className="pointer-events-none absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-4 w-4" aria-hidden />
                </span>
              ),
            }
          : {}),
      }))}
      onOpen={(t) => {
        const photo = album.photos.find((x) => x.photoId === t.id);
        if (photo) onToggle(photo.photoId, !photo.selected);
      }}
      emptyText="Album chưa có ảnh nào."
    />
  );
}

/**
 * Thanh chốt dính đáy màn.
 *
 * Album vài trăm tấm mà nút chốt nằm cuối trang thì khách phải cuộn hết mới
 * bấm được — và số đang chọn cũng biến mất khỏi tầm mắt lúc họ cần nó nhất.
 */
function CommitBar({
  selectedQty,
  busy,
  error,
  onCommit,
}: {
  selectedQty: number;
  busy: boolean;
  error: string;
  onCommit: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 border-t bg-background/95 p-3 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
        <span className="text-sm">
          Đã chọn <strong>{selectedQty}</strong> ảnh
        </span>
        <Button
          type="button"
          size="lg"
          className="min-h-[48px]"
          disabled={busy || selectedQty === 0}
          onClick={onCommit}
        >
          Chốt danh sách
        </Button>
      </div>
      {error && <p className="mx-auto mt-2 max-w-5xl text-sm text-destructive">{error}</p>}
    </div>
  );
}

function Message({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
      <ImageOff className="h-12 w-12 text-muted-foreground" aria-hidden />
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-muted-foreground">{detail}</p>
    </div>
  );
}

function SelectionSkeleton() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6">
      <Skeleton className="mb-4 h-9 w-72" />
      {/* Khung chờ so le chứ KHÔNG cùng một khổ: lưới thật ôm đúng khổ từng
          tấm, nên khung chờ vuông vắn đều tăm tắp là hứa một bố cục rồi đổi
          ngay khi ảnh về. */}
      <div className="flex gap-[2px]">
        {[0, 1].map((lan) => (
          <div key={lan} className="flex min-w-0 flex-1 flex-col gap-[2px]">
            {[3 / 4, 4 / 3, 2 / 3, 1].map((tiLe, i) => (
              <Skeleton key={i} className="w-full" style={{ aspectRatio: String(tiLe) }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
